import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from '@/components/ui/use-toast';
import { ControllerRenderProps } from 'react-hook-form';
import type React from 'react';
import {
  AccountCancelledReason,
  AccountProduct,
  AccountProfileInviteRole,
  AccountStatusLogType,
  AccountSuspendedReason,
  AccountTier,
  Maybe,
  OccupancyType,
  PricingEngineConfigLoanProductInclusionStatus,
  ProductCategory,
  ResidenceType,
  State,
} from '@/app/gql/sdk';
import { format, isFuture, parseISO } from 'date-fns';
import { NewResidenceType } from './enumDisplays';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import { QueryClient } from '@tanstack/react-query';
import numeral from 'numeral';

/** Strip __typename from query formValues so form state is safe to send in mutations. */
export function stripTypename<T>(obj: T): T {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripTypename) as T;
  const out = { ...obj } as Record<string, unknown>;
  delete out.__typename;
  for (const key of Object.keys(out)) {
    if (out[key] != null && typeof out[key] === 'object') {
      out[key] = stripTypename(out[key]);
    }
  }
  return out as T;
}

//custom auth errors
export enum FlashAuthError {
  SUSPENDED_ACCOUNT_PAST_DUE = 'SUSPENDED_ACCOUNT_PAST_DUE',
  EXPIRED_TRIAL_ACCOUNT = 'EXPIRED_TRIAL_ACCOUNT',
  INACTIVE_USER = 'INACTIVE_USER',
  INVALID_IMPERSONATION_TOKEN = 'INVALID_IMPERSONATION_TOKEN',
  AccessDenied = 'AccessDenied',
}

const dayjsExt = dayjs;

dayjs.extend(duration);
dayjs.extend(utc);

export { dayjsExt };

export function formatCommTimestamp(iso: string): string {
  const d = dayjs(iso);
  const now = dayjs();
  const diffMins = now.diff(d, 'minute');

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (d.isSame(now, 'day')) return d.format('h:mma');
  if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isSame(now, 'year')) return d.format('MMM D');
  return d.format('MMM D, YYYY');
}

export function formatExactTimestamp(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}

export const queryClient = new QueryClient();

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type NonFieldError = Maybe<
  { __typename?: 'Error' } & Pick<Error, 'message'> & { code?: string | null }
>;

export const classNames = cn;

export function showErrorToast(message?: string) {
  toast({
    variant: 'destructive',
    title: 'Something went wrong...',
    description: message || 'Please contact support.',
  });
}

export async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function cleanFieldValue(field: ControllerRenderProps<any, string>) {
  return { ...field, value: field.value === null ? undefined : field.value };
}

export function formatCurrency(
  value: string | number,
  options?: Intl.NumberFormatOptions
): string {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...options,
  });
  return currencyFormatter.format(Number(value));
}

export const formatPercent = (value: number | null | undefined) =>
  value != null ? numeral(value / 100).format('0.000%') : null;

export const AccountTierDisplay: { [key in AccountTier]: string } = {
  BROKERAGE: 'Brokerage',
  BUSINESS: 'Business',
  CORE: 'Core',
  FOUNDATION: 'Foundation',
  STARTER: 'Starter',
  PRODUCER: 'Producer',
  TRIAL: 'Trial',
};

export const NewResidenceTypeDisplay: { [key in NewResidenceType]: string } = {
  single_family: 'Single family',
  condo: 'Condo',
  coop: 'Co-op',
  multi_family: 'Multi family',
  other: 'Other',
};

export const ResidenceTypeDisplay: { [key in ResidenceType]: string } = {
  single_family: 'Single family',
  condo: 'Condo',
  coop: 'Co-op',
  multi_family: 'Multi family',
  other: 'Other',
  manufactured: 'Manufactured',
  townhouse: 'Townhouse',
};

export const ProductCategoryDisplay: {
  [key in ProductCategory]: { label: string; short: string };
} = {
  [ProductCategory.conventional]: { label: 'Conventional', short: 'Conv.' },
  [ProductCategory.homeready]: { label: 'HomeReady', short: 'HomeReady' },
  [ProductCategory.fha]: { label: 'FHA', short: 'FHA' },
  [ProductCategory.va]: { label: 'VA', short: 'VA' },
  [ProductCategory.usda]: { label: 'USDA', short: 'USDA' },
  [ProductCategory.jumbo]: { label: 'Jumbo', short: 'Jumbo' },
  [ProductCategory.dscr]: { label: 'DSCR', short: 'DSCR' },
  [ProductCategory.alt_doc]: { label: 'Alt Doc', short: 'Alt Doc' },
};

export const OccupancyTypeDisplay: { [key in OccupancyType]: string } = {
  primary: 'Primary home',
  secondary: 'Secondary home',
  investment: 'Investment home',
};

export function safeIsFuture(str: string) {
  if (!str) return;
  try {
    return isFuture(new Date(str));
  } catch (e) {
    console.log('Error parsing date', str, e);
  }
}

export function safeFormat(str: string, _format: string) {
  if (!str) return;
  try {
    return format(parseISO(str), _format);
  } catch (e) {
    console.log('Error parsing date', str, e);
  }
}

export function capitalizeFirstLetter(input: string): string {
  if (input.length === 0) {
    return input; // Return the input unchanged if it's an empty string
  }

  return input.charAt(0).toUpperCase() + input.slice(1);
}

export const AccountProductTypeDisplay: {
  [key in AccountProduct]: { name: string; backgroundColor: string };
} = {
  [AccountProduct.FALCON]: { name: 'Falcon', backgroundColor: 'bg-yard-300' },
  [AccountProduct.WHITE_LABEL]: {
    name: 'White-Label Domains',
    backgroundColor: 'bg-fireplace-400',
  },
  [AccountProduct.WIDGET_API_ACCESS]: {
    name: 'Widgets & API Access',
    backgroundColor: 'bg-sunroom-400',
  },
};

export const AccountProfileInviteRoleDisplay: {
  [key in AccountProfileInviteRole]?: { name: string; textColor?: string };
} = {
  [AccountProfileInviteRole.AUTHROLE_DEPRECATED_SUPERUSER]: {
    name: 'Superuser',
  },
  [AccountProfileInviteRole.AUTHROLE_DEPRECATED_MORTY_INTERNAL]: {
    name: 'Morty Internal',
  },
  [AccountProfileInviteRole.AUTHROLE_DEPRECATED_ACCOUNT_OWNER]: {
    name: 'Account Owner',
  },
  [AccountProfileInviteRole.AUTHROLE_DEPRECATED_ACCOUNT_MEMBER]: {
    name: 'Account Member',
  },
};

export function maskString(str: string): string {
  if (str.length <= 5) {
    return str; // If the string has 5 or fewer characters, no masking needed
  }

  const firstChar = str[0];
  const lastFourChars = str.slice(-4); // Get the last 4 characters of the string

  // Create a masked string with * for characters between the first and last 4
  const maskedChars = '*'.repeat(10);

  // Concatenate the first character, masked characters, and last four characters
  return firstChar + maskedChars + lastFourChars;
}

export function getInitials(fullName: string): string {
  // Split the full name into an array of words
  const words = fullName.split(' ').filter((word) => word);

  // Get the first letter of each word and join them
  const initials = words.map((word) => word.charAt(0)).join('');

  // Convert the result to uppercase
  return initials.toUpperCase();
}

export function titleCase(str: string): string {
  if (str.includes(' ')) {
    const arr = str.split(' ').filter((word) => word);
    const full = arr
      .map((str) => `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`)
      .join(' ');
    return full;
  } else {
    return `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`;
  }
}

const SI_SYMBOL = ['', 'k', 'm', 'g', 't', 'p', 'e'];

export function abbreviateNumber(number: number) {
  const tier = (Math.log10(Math.abs(number)) / 3) | 0;

  if (tier == 0) return number;

  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = number / scale;

  return scaled.toFixed(1) + suffix;
}

export const PricingEngineConfigLoanProductInclusionStatusDisplay: {
  [key in PricingEngineConfigLoanProductInclusionStatus]: string;
} = {
  [PricingEngineConfigLoanProductInclusionStatus.EXCLUDED]: 'Exclude',
  [PricingEngineConfigLoanProductInclusionStatus.INCLUDED]: 'Include',
  [PricingEngineConfigLoanProductInclusionStatus.INTERNAL_ONLY]:
    'Internal only',
};

export const StateDisplay: { [key in State]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AS: 'American Samoa',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District Of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  MP: 'Northern Mariana Islands',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VI: 'Virgin Islands',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export function isBasicTier(tier: AccountTier) {
  return tier === AccountTier.FOUNDATION || tier === AccountTier.STARTER;
}
export function isTrialTier(tier: AccountTier) {
  return tier === AccountTier.TRIAL;
}

export const AccountTiersSorted: AccountTier[] = [
  AccountTier.STARTER,
  AccountTier.FOUNDATION,
  AccountTier.PRODUCER,
  AccountTier.BUSINESS,
  AccountTier.BROKERAGE,
  AccountTier.TRIAL,
];
export const AccountStatusLogTypeSorted: AccountStatusLogType[] = [
  AccountStatusLogType.NEW,
  AccountStatusLogType.ONBOARDING,
  AccountStatusLogType.ACTIVE,
  AccountStatusLogType.SUSPENDED,
  AccountStatusLogType.CANCELLED,
];
export const AccountStatusCancelledOtherReasonsSorted: AccountCancelledReason[] =
  [
    AccountCancelledReason.FAILED_TO_PAY,
    AccountCancelledReason.TERMINATED_FOR_CAUSE,
    AccountCancelledReason.VOLUNTARY_DEPARTURE,
    AccountCancelledReason.OTHER,
  ];
export const AccountStatusSuspendedOtherReasonsSorted: AccountSuspendedReason[] =
  [
    AccountSuspendedReason.PAYMENT_PAST_DUE,
    AccountSuspendedReason.TRIAL_EXPIRED,
    AccountSuspendedReason.OTHER,
  ];

export enum FileMimeType {
  PDF = 'application/pdf',
  XML = 'application/xml',
}

export const downloadBase64File = (
  base64Data: string,
  fileName: string,
  mimeType: FileMimeType = FileMimeType.PDF
) => {
  const linkSource = `data:${mimeType};base64,${base64Data}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

/**
 * Converts a nullable boolean value to a strict true/false/null
 * @param value The boolean value to normalize
 * @returns true, false, or null
 */
export const normalizeBoolean = (
  value: boolean | null | undefined
): boolean | null => {
  if (value === null || value === undefined) return null;
  return value === true;
};

export const formatCreditReportFileName = (
  name: string,
  date: string
): string => {
  const cleanName = name
    .replace(/[^a-zA-Z0-9]/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();
  const formattedDate = dayjs(date).format('YYYY-MM-DD');
  return `${cleanName}-credit-report-${formattedDate}.pdf`;
};

/**
 * Removes all falsey values from an array.
 * @param array The array to compact
 * @returns A new array with all falsey values removed
 */
export function compact<T>(array?: T[]): NonNullable<T>[] {
  return array?.filter((item): item is NonNullable<T> => Boolean(item)) ?? [];
}

type PropertyKey = string | number | symbol;
type ValueIteratee<T> = ((item: T) => PropertyKey) | string | string[];

/**
 * Groups an array of elements
 * @param array The array to group
 * @param iteratee Function, property name, or array of property names
 * @returns An object with the grouped elements
 */
export function groupBy<T>(
  array: T[],
  iteratee: ValueIteratee<T>
): Record<PropertyKey, T[]> {
  const getKey = (item: T): PropertyKey => {
    if (typeof iteratee === 'function') {
      return iteratee(item);
    }
    if (typeof iteratee === 'string') {
      return iteratee.split('.').reduce((obj, key) => obj?.[key], item as any);
    }
    if (Array.isArray(iteratee)) {
      return iteratee
        .map((path) =>
          path.split('.').reduce((obj, key) => obj?.[key], item as any)
        )
        .join('_');
    }
    throw new Error('Invalid iteratee');
  };

  return array.reduce(
    (result, value) => {
      const key = getKey(value);
      (result[key] || (result[key] = [])).push(value);
      return result;
    },
    {} as Record<PropertyKey, T[]>
  );
}

/**
 * Checks if value is null or undefined
 * @param value The value to check
 * @returns True if the value is null or undefined
 */
export const isNil = (value: unknown): value is null | undefined =>
  value == null;

type PropertyName = string | number | symbol;
type Many<T> = T | ReadonlyArray<T>;

/**
 * Creates an object composed of the own enumerable properties that are not omitted
 * @param object The source object
 * @param paths The property paths to omit
 * @returns Returns the new object
 */
function omit<T extends object, K extends PropertyName[]>(
  object: T | null | undefined,
  ...paths: K
): Pick<T, Exclude<keyof T, K[number]>>;
function omit<T extends object, K extends keyof T>(
  object: T | null | undefined,
  ...paths: Array<Many<K>>
): Omit<T, K>;
function omit<T extends object>(
  object: T | null | undefined,
  ...paths: Array<Many<PropertyName>>
): Partial<T>;
function omit(obj: any, ...paths: Array<Many<PropertyName>>) {
  if (obj == null) return {};

  const keysToOmit = new Set(paths.flat());
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysToOmit.has(key))
  );
}

/**
 * Creates a duplicate-free version of an array, using SameValueZero for equality comparisons.
 * The first occurrence of each element is kept.
 * @param array The array to inspect
 * @returns Returns the new duplicate free array
 */
function uniq<T>(array: T[] | null | undefined): T[];
function uniq(array: any[] | null | undefined): any[] {
  if (array == null) {
    return [];
  }

  return Array.from(new Set(array));
}

/**
 * Checks if value is classified as a Number primitive or object.
 * Note: This includes Infinity, -Infinity, and NaN
 * @param value The value to check
 * @returns Returns true if value is correctly classified, else false
 */
export function isNumber(value?: any): value is number {
  return typeof value === 'number' || value instanceof Number;
}

export { omit, uniq };

export const truncateToDecimals = (num: number, dec = 3) => {
  const calcDec = Math.pow(10, dec);
  return Math.trunc(num * calcDec) / calcDec;
};

export const getPercentageValue = (
  value?: number | null,
  total?: number | null
) => {
  if (!value || !total) return undefined;
  const floatPercent = (value / total) * 100;
  const trunc = truncateToDecimals(floatPercent);
  return floatPercent % 1 === 0 ? floatPercent : trunc;
};

export const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatTrialExpiryDate = (isoDate: string): string => {
  const date = dayjs(isoDate);
  const day = date.date();
  const suffix = getOrdinalSuffix(day);

  return date.format(`MMM D[${suffix}] [at] h:mm A`);
};

export function getAuthErrorFromFlash(
  flash: string | string[] | undefined
): FlashAuthError | null {
  if (!flash) return null;
  const errors = Object.values(FlashAuthError);

  if (typeof flash === 'string') {
    for (const error of errors) {
      if (flash.includes(error)) {
        return error;
      }
    }
  }

  if (Array.isArray(flash)) {
    for (const flashItem of flash) {
      for (const error of errors) {
        if (flashItem.includes(error)) {
          return error;
        }
      }
    }
  }

  return null;
}

export const omitTypenameDeep = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(omitTypenameDeep);
  } else if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([key]) => key !== '__typename')
        .map(([key, value]) => [key, omitTypenameDeep(value)])
    );
  }
  return obj;
};

export function camelCase(input: string): string {
  const s = input.trim();
  if (!s) return s;
  const lower = s.toLowerCase();
  return lower.replace(/[-_\s]+([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

type ScrollIntoViewOptions = {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
  offset?: number;
};

/**
 * Scrolls an element into view with smooth animation
 * @param element - The element or ref to scroll into view
 * @param options - Scroll options (behavior, block position, offset)
 * @returns void
 */
export function scrollIntoView(
  element: HTMLElement | null | undefined | React.RefObject<HTMLElement | null>,
  options: ScrollIntoViewOptions = {}
): void {
  const { behavior = 'smooth', block = 'end', inline, offset = 0 } = options;

  const targetElement =
    element && 'current' in element ? element.current : element;

  if (!targetElement) {
    return;
  }

  if (offset === 0) {
    targetElement.scrollIntoView({
      behavior,
      block,
      inline,
    });
  } else {
    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY + offset;

    window.scrollTo({
      top: offsetPosition,
      behavior,
    });
  }
}

type ScrollToOptions = {
  top?: number;
  left?: number;
  behavior?: ScrollBehavior;
};

/**
 * Scrolls the window to a specific position
 * @param options - Scroll options (top, left, behavior)
 * @returns void
 */
export function scrollTo(options: ScrollToOptions = {}): void {
  const { top = 0, left = 0, behavior = 'smooth' } = options;

  const scrollableElement =
    document.scrollingElement || document.documentElement || document.body;

  requestAnimationFrame(() => {
    setTimeout(() => {
      window.scrollTo({
        top,
        left,
        behavior,
      });

      if (scrollableElement) {
        scrollableElement.scrollTo({
          top,
          left,
          behavior,
        });
      }
    }, 0);
  });
}

/**
 * Scrolls the window to the top with smooth animation
 * @param options - Optional scroll behavior and delay
 * @returns void
 */
export function scrollToTop(
  options: { behavior?: ScrollBehavior; delay?: number } = {}
): void {
  const { behavior = 'smooth', delay = 0 } = options;

  if (delay > 0) {
    setTimeout(() => {
      scrollTo({ top: 0, behavior });
    }, delay);
  } else {
    scrollTo({ top: 0, behavior });
  }
}
